/* eslint-disable prettier/prettier */
import React, { createContext, useContext, useRef, useState, useCallback } from 'react'

type WebSocketCtx = {
  ws: React.MutableRefObject<WebSocket | null>;
  isReady: boolean;
  connect: (url: string) => void;
  disconnect: () => void;
  send: (data: string | ArrayBufferLike | Blob | ArrayBufferView) => void;
  audioStream: MediaStream | null;
  audioContextRef: React.MutableRefObject<AudioContext | null>;
  sourceRef: React.MutableRefObject<MediaStreamAudioSourceNode | null>;
  processorRef: React.MutableRefObject<ScriptProcessorNode | null>;
  setAudioStream: React.Dispatch<React.SetStateAction<MediaStream | null>>;
};

const WebSocketContext = createContext<WebSocketCtx | undefined>(undefined)

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ws = useRef<WebSocket | null>(null)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const [isReady, setIsReady] = useState(false)

  const connect = useCallback((url: string) => {
    if (ws.current) ws.current.close()
    ws.current = new WebSocket(url)
    ws.current.onopen = () => {setIsReady(true); console.log('WebSocket de transcrição conectado')}
    ws.current.onclose = () => {setIsReady(false); console.log('WebSocket de transcrição fechado')}
    ws.current.onerror = () => setIsReady(false)
  }, [])

  const disconnect = useCallback(() => {
    if (ws.current && ws.current.readyState === 1) {
        ws.current.send(JSON.stringify({ type: 'endSession' }))
        ws.current.close()
    }
    ws.current = null
    setIsReady(false)
  }, [])

  const send = useCallback((data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(data)
    }
  }, [])

  return (
    <WebSocketContext.Provider value={{ ws, isReady, connect, disconnect, send, audioStream,
      audioContextRef,
      sourceRef,
      processorRef,
      setAudioStream }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocketContext() {
  const ctx = useContext(WebSocketContext)
  if (!ctx) throw new Error('useWebSocketContext must be used within a WebSocketProvider')
  return ctx
}
